import { useState } from "react";
import { useMutation } from "urql";
import { CancelInterviewMutation } from "../../graphql/interviews.graphql";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { formatInterviewDate, formatInterviewTime } from "./schedule";

interface CancelInterviewButtonProps {
  interviewId: string;
  candidateName: string | null;
  schedule?: { startTime: string; endTime: string };
  sponsorName?: string | null;
  onCancelled?: () => void;
}

export function CancelInterviewButton({
  interviewId,
  candidateName,
  schedule,
  sponsorName,
  onCancelled,
}: CancelInterviewButtonProps) {
  const [confirming, setConfirming] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [{ fetching }, executeCancelInterview] = useMutation(
    CancelInterviewMutation,
  );

  const handleCancel = async () => {
    setErrorMessage(null);
    const result = await executeCancelInterview({ interviewId });

    if (result.error) {
      // CombinedError.message は "[GraphQL] " 等の接頭辞が付くため、GraphQL エラー本文を優先して表示する
      setErrorMessage(
        result.error.graphQLErrors[0]?.message ??
          "キャンセルに失敗しました。時間をおいて再度お試しください。",
      );
      return;
    }

    setConfirming(false);
    onCancelled?.();
  };

  const handleOpenChange = (open: boolean) => {
    // 送信中に閉じるとエラー表示先が失われ、失敗が利用者に伝わらなくなる
    if (fetching) return;
    setConfirming(open);
    if (!open) setErrorMessage(null);
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setConfirming(true)}
        className="text-[var(--color-red-500)] text-xs hover:text-[var(--color-red-600)]"
      >
        予約をキャンセル
      </Button>
      <Dialog open={confirming} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>予約をキャンセルしますか？</DialogTitle>
            <DialogDescription>
              {candidateName ? `${candidateName} さんの` : "この"}
              インタビュー予約をキャンセルし、Zoom ミーティングを削除します。
              インタビュアーと候補者の双方にキャンセル通知メールが送られ（候補者宛には再予約用の日程調整ページ
              URL
              を記載）、候補者は日程調整ページから再度予約できるようになります。
            </DialogDescription>
          </DialogHeader>
          {(schedule || sponsorName) && (
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
              {schedule && (
                <>
                  <dt className="text-[var(--color-text-secondary)]">日程</dt>
                  <dd className="text-[var(--color-text-primary)]">
                    {formatInterviewDate(schedule.startTime)}{" "}
                    {formatInterviewTime(schedule.startTime)} -{" "}
                    {formatInterviewTime(schedule.endTime)}
                  </dd>
                </>
              )}
              {sponsorName && (
                <>
                  <dt className="text-[var(--color-text-secondary)]">会社</dt>
                  <dd className="text-[var(--color-text-primary)]">
                    {sponsorName}
                  </dd>
                </>
              )}
            </dl>
          )}
          {errorMessage && (
            <p className="text-[var(--color-red-500)] text-sm">
              {errorMessage}
            </p>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={fetching}
            >
              閉じる
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancel}
              disabled={fetching}
            >
              {fetching ? "キャンセル中..." : "予約をキャンセルする"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
